import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Title, Text, CenteredColumn, Button } from '@/shared/ui';
import { RgBody, RgFoot } from '@/shared/borisFields';
import type { UserData } from '@/types/registration';
import { StartOverModal } from '@/components/StartOverModal';
import { PrivacyPolicyModal } from '@/components/modals/PrivacyPolicyModal';
import { CareForHeartConsentModal } from '@/components/modals/CareForHeartConsentModal';
import DisclaimersCheckboxes, { Disclaimers } from './DisclaimersCheckboxes';
import CmsConsentCheckboxes, { type CmsConsents, CONSENT_KEYS } from './CmsConsentCheckboxes';
import { OtpCodeInput } from './OtpCodeInput';
import { useRegistration } from '@/context/RegistrationContext';
import { useApiError } from '@/context/ApiErrorContext';
import { useAnalytics } from '@/hooks';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { VIEW_SIGNUP } from '@/consts/events';
import { sendOtp, verifyOtp } from '@/services/otp';

const OTP_LENGTH = 6;

// Boris `.rg-screen`: fills the frame's main column (flex:1/min-height:0
// against Layout's FlowMain) and splits into RgBody (scrolls) + RgFoot (pinned
// flex sibling), matching PasswordAccountForm/MedicareCoverageStep so the
// verify CTA stays pinned like Boris's ConsentScreen `.rg-foot` instead of
// just trailing the consent list.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

// Boris `.rg-head-block`: title + subtitle both left-aligned, 12px apart.
const TitleStyled = styled(Title)`
  text-align: left;
  width: 100%;
  margin-bottom: ${Spacing.x1_5}px !important;
`;

const SubStyled = styled(Text)`
  width: 100%;
  text-align: left;
`;

// Boris DS tertiary at size S (`.dsb-s.dsb-tertiary`): 40px pill, coral text,
// coral-3 hover tint, coral-5 active. The shared Button has no Boris-coral
// tertiary (its variants are teal or the off-palette legacy pink-red), so the
// resend link is styled directly against the Happy Hearts coral tokens.
const ResendButton = styled.button`
  align-self: center;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${BorisScreener.coral};
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    background: #ffebe6;
  }
  &:active:not(:disabled) {
    background: #f7d5ce;
    color: ${BorisScreener.coralPressed};
  }
  &:disabled {
    opacity: 0.38;
    cursor: default;
  }
`;

const ConsentCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  background: ${BorisScreener.white};
  border: 1px solid ${BorisScreener.line};
  border-radius: 16px;
  padding: ${Spacing.x2}px;
  margin: ${Spacing.x3}px 0;
`;

const maskPhone = (phone: string) => (phone ? `•••• ${phone.slice(-4)}` : '');

// RND-35692 — flag-on account creation (phone/OTP, no password). SCAFFOLD:
// the send/verify calls and the post-verify account creation are stubbed
// (TODO(blocked-Haim)); gated OFF by default via VITE_PASSWORDLESS_OTP_ENABLED.
const OtpAccountStep: React.FC = () => {
  const navigate = useNavigate();
  const { registrationData, setUserData } = useRegistration();
  const { medicareCoverage, account, promoCodeData } = registrationData;
  const phone = account.phoneNumber;

  const { t } = useTranslation();
  const { trackView } = useAnalytics();
  const { showApiError } = useApiError();

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [disclaimers, setDisclaimers] = useState<Disclaimers>({
    enableSmsMarketing: false,
    privacyPracticeReviewed: false,
    consentToShareData: false,
  });
  const [cmsConsents, setCmsConsents] = useState<CmsConsents>({
    voluntary: false,
    apiData: false,
    pcpSharing: false,
    controlGroup: false,
  });

  const { resetBlocker } = useAppFlow(STEP_KEYS.CREATE_ACCOUNT, {
    onBackNavigationBlocked: () => setShowStartOverModal(true),
    autoNavigateOnComplete: true,
  });

  const isCareForHeartConsentRequired = promoCodeData?.appConfig?.medAdvisors !== 'off';
  const allCmsConsentsChecked = CONSENT_KEYS.every((key) => cmsConsents[key]);
  const isVerifyDisabled =
    code.length !== OTP_LENGTH ||
    !allCmsConsentsChecked ||
    (isCareForHeartConsentRequired && !disclaimers.privacyPracticeReviewed);

  // Fire the signup view + send the OTP once when the step mounts
  // (TODO(blocked-Haim): real send). Both sit behind sentRef so a re-render
  // (e.g. trackView not being referentially stable) can neither re-count the
  // view event nor re-send the code and re-text the user.
  const sentRef = useRef(false);
  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    trackView({ event: VIEW_SIGNUP });
    void sendOtp(phone);
  }, [trackView, phone]);

  const handleResend = () => {
    if (isLoading) return;
    void sendOtp(phone);
  };

  const handleVerify = async () => {
    setIsLoading(true);
    try {
      // TODO(blocked-Haim): real verify + passwordless register. The stub
      // returns a sentinel token so the dev flow advances.
      const { authToken } = await verifyOtp(phone, code);

      // TODO(blocked-Haim): real user fields + $alignment wiring (needs a real
      // token). The password path in PasswordAccountForm still does this via
      // apiClient.signup + cmsApiClient.postAlignment; replicate once the
      // passwordless-register response shape is known.
      const userData: UserData = {
        authToken,
        solutions: [],
        email: account.email,
        phonenumber: phone,
        firstName: medicareCoverage.firstName,
        lastName: medicareCoverage.lastName,
        analyticsID: '',
      };
      setUserData(userData);
    } catch {
      // TODO(blocked-Haim): real per-error-code messaging (bad/expired code,
      // rate limit) + failure analytics arrive with the OTP contract.
      showApiError();
    } finally {
      setIsLoading(false);
    }
  };

  const verifyVariant = isLoading ? 'loading' : isVerifyDisabled ? 'disabled' : 'brand-primary';

  return (
    <Wrapper>
      <RgBody>
        <CenteredColumn span={8}>
          <TitleStyled size="h2">{t('create-account.otp.title')}</TitleStyled>
          <SubStyled size="p1">{t('create-account.otp.subtitle', { phone: maskPhone(phone) })}</SubStyled>
        </CenteredColumn>

        <CenteredColumn span={8} marginTop={Spacing.x3}>
          <OtpCodeInput
            value={code}
            onChange={setCode}
            length={OTP_LENGTH}
            disabled={isLoading}
            label={t('create-account.otp.digit-label')}
          />
        </CenteredColumn>

        <CenteredColumn span={8}>
          <ConsentCard>
            <CmsConsentCheckboxes
              consents={cmsConsents}
              onChange={(key, value) => setCmsConsents((prev) => ({ ...prev, [key]: value }))}
            />
            <DisclaimersCheckboxes
              disclaimers={disclaimers}
              setDisclaimers={setDisclaimers}
              onPrivacyClick={() => setShowPrivacyModal(true)}
              onInformedConsentClick={() => setShowConsentModal(true)}
              isCareForHeartConsentRequired={isCareForHeartConsentRequired}
            />
          </ConsentCard>
        </CenteredColumn>
      </RgBody>

      <RgFoot>
        <CenteredColumn span={6}>
          <ResendButton type="button" disabled={isLoading} onClick={handleResend}>
            {t('create-account.otp.resend')}
          </ResendButton>
          <Button
            variant={verifyVariant}
            size="default"
            text={t('create-account.otp.verify')}
            onClick={() => {
              void handleVerify();
            }}
          />
        </CenteredColumn>
      </RgFoot>

      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={() => {
          setShowStartOverModal(false);
          navigate('/');
        }}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
      <PrivacyPolicyModal open={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <CareForHeartConsentModal open={showConsentModal} onClose={() => setShowConsentModal(false)} />
    </Wrapper>
  );
};

export { OtpAccountStep };
